import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FaCheck,
  FaChevronDown,
  FaChevronUp,
  FaEye,
  FaEyeSlash,
  FaRobot,
  FaSave,
  FaShieldAlt,
  FaSync,
  FaTimes,
  FaTrashAlt,
} from "react-icons/fa";
import { useAuth } from "../../context/AuthContext";
import { isPlatformAdmin } from "../../constants/roles";
import "./ApiKeysSettings.css";

/* ─── Category & provider definitions ───────────────────────── */

const CATEGORIES = [
  {
    id: "ai",
    title: "AI Providers",
    description:
      "Choose the provider and model used across all AI-assisted features. Set an API key, pick a model, then click Make Default to activate a provider platform-wide. Use the switch above to turn AI on or off everywhere.",
    providers: [
      { key: "openai",    label: "OpenAI",            abbrev: "OAI", hint: "Powers GPT models (gpt-4o, gpt-4.1, …)." },
      { key: "anthropic", label: "Anthropic",          abbrev: "ANT", hint: "Powers Claude models (claude-sonnet, claude-opus, …)." },
      { key: "google",    label: "Google Gemini",      abbrev: "GEM", hint: "Powers Gemini models (gemini-2.0-flash, gemini-1.5-pro, …)." },
    ],
    type: "ai",
  },
  {
    id: "osint",
    title: "OSINT & Threat Intelligence",
    description:
      "API credentials for external threat intelligence services used in URL, domain, IP, and file reputation lookups.",
    providers: [
      { key: "vt",        label: "VirusTotal",        abbrev: "VT",  hint: "URL, domain, IP, and file lookups." },
      { key: "abuseipdb", label: "AbuseIPDB",          abbrev: "AIP", hint: "IP address reputation and abuse reports." },
      { key: "ipqs",      label: "IPQualityScore",     abbrev: "IQS", hint: "URL and IP fraud / risk scoring." },
      { key: "urlscan",   label: "URLScan.io",         abbrev: "USN", hint: "Web page scanning and screenshot analysis." },
      { key: "otx",       label: "AlienVault OTX",     abbrev: "OTX", hint: "Open threat exchange pulse lookups." },
    ],
    type: "key-only",
  },
  {
    id: "sandbox",
    title: "Advanced Analysis",
    description:
      "Sandbox environments for detonating email attachments. Configure at least one key, then set a default sandbox for automated detonation workflows.",
    providers: [
      { key: "hybrid_analysis", label: "Hybrid Analysis", abbrev: "HA",  hint: "Falcon Sandbox — deep static and dynamic malware analysis." },
      { key: "anyrun",          label: "ANY.RUN",          abbrev: "AR",  hint: "Interactive cloud sandbox for real-time detonation." },
    ],
    type: "sandbox",
  },
  {
    id: "platform",
    title: "Platform",
    description:
      "Internal platform tokens distributed to end-users or integrated tools.",
    providers: [
      { key: "outlook_addin", label: "Outlook Add-in", abbrev: "OL", hint: "Bearer token required by the PhishMatrix Outlook add-in." },
    ],
    type: "key-only",
  },
];

const ALL_OPTION = { id: "all", title: "All Categories" };

const SOURCE_BADGE = {
  db:   { label: "DB",   cls: "ik-src-db"   },
  env:  { label: ".env", cls: "ik-src-env"  },
  none: { label: "Not set", cls: "ik-src-none" },
};

/* ─── Component ─────────────────────────────────────────────── */

// `categories` optionally scopes which CATEGORIES this instance renders, so the
// same component can power both the "AI Configurations" page (AI only) and the
// "API Keys" page (everything else). Omit to show all.
export default function ApiKeysSettings({ categories = null }) {
  const { token, user } = useAuth();
  const isAdmin = isPlatformAdmin(user?.role);

  const scopedCategories = useMemo(
    () => (categories ? CATEGORIES.filter((c) => categories.includes(c.id)) : CATEGORIES),
    [categories]
  );
  const showFilterBar = scopedCategories.length > 1;

  /* API key status */
  const [keyStatus, setKeyStatus]     = useState({});
  const [loading, setLoading]         = useState(true);
  const [globalError, setGlobalError] = useState("");

  /* Category filter */
  const [activeCat, setActiveCat] = useState("all");

  /* Card expansion (set of provider keys) */
  const [expanded, setExpanded] = useState(new Set());

  /* Per-provider key editing */
  const [editDraft, setEditDraft] = useState({});
  const [reveal, setReveal]       = useState({});
  const [keyBusy, setKeyBusy]     = useState(null);
  const [keyError, setKeyError]   = useState({});

  /* Master AI on/off switch (platform-wide) */
  const [aiEnabled, setAiEnabled]         = useState(true);
  const [aiEnabledSaving, setAiEnabledSaving] = useState(false);
  const [aiEnabledMsg, setAiEnabledMsg]   = useState("");

  /* AI provider settings */
  const [defaultAiProvider, setDefaultAiProvider] = useState("openai");
  const [defaultAiModel, setDefaultAiModel]       = useState(null);
  const [aiModels, setAiModels]                   = useState({});
  const [aiModelsLoading, setAiModelsLoading]     = useState({});
  const [aiModelsError, setAiModelsError]         = useState({});
  const [selectedModel, setSelectedModel]         = useState({});
  const [aiSaving, setAiSaving]   = useState(null);
  const [aiSaveMsg, setAiSaveMsg] = useState({});

  /* Sandbox default */
  const [sandboxDefault, setSandboxDefault] = useState("");
  const [sandboxSaving, setSandboxSaving]   = useState(null);
  const [sandboxMsg, setSandboxMsg]         = useState({});

  /* ── Data fetchers ── */

  const fetchKeyStatus = useCallback(async () => {
    setLoading(true);
    setGlobalError("");
    try {
      const res = await fetch("/api/admin/api-keys", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      const map = {};
      for (const p of json.providers || []) map[p.provider] = p;
      setKeyStatus(map);
    } catch (err) {
      setGlobalError(err.message || "Failed to load API keys");
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchAiSettings = useCallback(async () => {
    try {
      const res = await fetch("/api/admin/ai-settings", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const json = await res.json();
      setDefaultAiProvider(json.provider || "openai");
      setDefaultAiModel(json.model || null);
      setAiEnabled(json.enabled !== false);
    } catch { /* silent */ }
  }, [token]);

  const toggleAiEnabled = useCallback(async () => {
    const next = !aiEnabled;
    setAiEnabledSaving(true);
    setAiEnabledMsg("");
    // Optimistic flip so the switch feels instant; revert on failure.
    setAiEnabled(next);
    try {
      const res = await fetch("/api/admin/ai-enabled", {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ enabled: next }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
      setAiEnabled(json.enabled !== false);
      setAiEnabledMsg(json.enabled !== false
        ? "AI features enabled platform-wide."
        : "AI features disabled platform-wide.");
      setTimeout(() => setAiEnabledMsg(""), 3500);
    } catch (err) {
      setAiEnabled(!next); // revert
      setAiEnabledMsg(`Error: ${err.message || "Failed to update AI status"}`);
    } finally {
      setAiEnabledSaving(false);
    }
  }, [aiEnabled, token]);

  const fetchSandboxDefault = useCallback(async () => {
    try {
      const res = await fetch("/api/detonations/providers");
      if (!res.ok) return;
      const json = await res.json();
      setSandboxDefault(json.default || "");
    } catch { /* silent */ }
  }, []);

  const fetchModels = useCallback(async (providerKey) => {
    setAiModelsLoading((p) => ({ ...p, [providerKey]: true }));
    setAiModelsError((p) => ({ ...p, [providerKey]: "" }));
    try {
      const res = await fetch(`/api/admin/ai-models?provider=${providerKey}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
      setAiModels((p) => ({ ...p, [providerKey]: json.models || [] }));
    } catch (err) {
      setAiModelsError((p) => ({ ...p, [providerKey]: err.message }));
    } finally {
      setAiModelsLoading((p) => ({ ...p, [providerKey]: false }));
    }
  }, [token]);

  useEffect(() => { fetchKeyStatus(); },      [fetchKeyStatus]);
  useEffect(() => { fetchAiSettings(); },     [fetchAiSettings]);
  useEffect(() => { fetchSandboxDefault(); }, [fetchSandboxDefault]);

  /* ── Card toggle ── */
  const toggleCard = (key, type) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
        if (type === "ai" && keyStatus[key]?.source !== "none") fetchModels(key);
      }
      return next;
    });
  };

  /* ── Key management ── */
  const startEdit  = (pk) => setEditDraft((p) => ({ ...p, [pk]: "" }));
  const cancelEdit = (pk) => {
    setEditDraft((p) => { const n = { ...p }; delete n[pk]; return n; });
    setReveal((p) => ({ ...p, [pk]: false }));
    setKeyError((p) => ({ ...p, [pk]: "" }));
  };

  const saveKey = async (pk) => {
    const value = (editDraft[pk] || "").trim();
    if (!value) { setKeyError((p) => ({ ...p, [pk]: "API key is required" })); return; }
    setKeyBusy(pk);
    setKeyError((p) => ({ ...p, [pk]: "" }));
    try {
      const res = await fetch(`/api/admin/api-keys/${pk}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ value }),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || `HTTP ${res.status}`); }
      const json = await res.json();
      setKeyStatus((prev) => ({ ...prev, [pk]: json.provider }));
      cancelEdit(pk);
      if (expanded.has(pk)) {
        const cat = CATEGORIES.find((c) => c.providers.some((p) => p.key === pk));
        if (cat?.type === "ai") fetchModels(pk);
      }
    } catch (err) {
      setKeyError((p) => ({ ...p, [pk]: err.message || "Failed to save" }));
    } finally {
      setKeyBusy(null);
    }
  };

  const clearKey = async (pk, label) => {
    if (!window.confirm(`Remove the saved key for ${label}? Falls back to .env if configured there.`)) return;
    setKeyBusy(pk);
    try {
      const res = await fetch(`/api/admin/api-keys/${pk}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || `HTTP ${res.status}`); }
      const json = await res.json();
      setKeyStatus((prev) => ({ ...prev, [pk]: json.provider }));
      cancelEdit(pk);
    } catch (err) {
      setKeyError((p) => ({ ...p, [pk]: err.message || "Failed to remove" }));
    } finally {
      setKeyBusy(null);
    }
  };

  /* ── AI default ── */
  const makeAiDefault = async (providerKey) => {
    const model =
      selectedModel[providerKey] !== undefined
        ? selectedModel[providerKey]
        : defaultAiProvider === providerKey
        ? defaultAiModel
        : null;
    setAiSaving(providerKey);
    setAiSaveMsg((p) => ({ ...p, [providerKey]: "" }));
    try {
      const res = await fetch("/api/admin/ai-settings", {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ provider: providerKey, model: model || null }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
      setDefaultAiProvider(providerKey);
      setDefaultAiModel(model || null);
      setAiSaveMsg((p) => ({ ...p, [providerKey]: "Saved as default." }));
      setTimeout(() => setAiSaveMsg((p) => ({ ...p, [providerKey]: "" })), 3000);
    } catch (err) {
      setAiSaveMsg((p) => ({ ...p, [providerKey]: `Error: ${err.message}` }));
    } finally {
      setAiSaving(null);
    }
  };

  /* ── Sandbox default ── */
  const makeSandboxDefault = async (providerKey) => {
    setSandboxSaving(providerKey);
    setSandboxMsg((p) => ({ ...p, [providerKey]: "" }));
    try {
      const res = await fetch("/api/detonations/default", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ provider: providerKey }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setSandboxDefault(providerKey);
      setSandboxMsg((p) => ({ ...p, [providerKey]: "Saved as default." }));
      setTimeout(() => setSandboxMsg((p) => ({ ...p, [providerKey]: "" })), 3000);
    } catch (err) {
      setSandboxMsg((p) => ({ ...p, [providerKey]: `Error: ${err.message}` }));
    } finally {
      setSandboxSaving(null);
    }
  };

  if (!isAdmin) {
    return (
      <div className="ik-forbidden">
        <FaShieldAlt />
        <p>This section is only available to administrators.</p>
      </div>
    );
  }

  /* ── Render helpers ── */

  const Monogram = ({ abbrev }) => (
    <span className="ik-monogram">{abbrev}</span>
  );

  const renderKeySection = (pk, label) => {
    const status     = keyStatus[pk] || { source: "none" };
    const isDrafting = Object.prototype.hasOwnProperty.call(editDraft, pk);
    const isBusy     = keyBusy === pk;
    const draft      = editDraft[pk] || "";
    const showText   = reveal[pk];

    return (
      <div className="ik-key-section">
        <span className="ik-field-label">API Key</span>

        {status.masked ? (
          <div className="ik-key-current">
            <code className="ik-masked">{status.masked}</code>
            {status.source === "db" && status.updated_by && (
              <span className="ik-audit">
                Updated by {status.updated_by} · {new Date(status.updated_at).toLocaleString()}
              </span>
            )}
          </div>
        ) : (
          <p className="ik-key-empty">No key configured</p>
        )}

        {!isDrafting ? (
          <div className="ik-row-actions">
            <button className="ik-btn ik-btn-outline" onClick={() => startEdit(pk)} disabled={isBusy}>
              {status.source === "db" ? "Replace key" : "Set key"}
            </button>
            {status.source === "db" && (
              <button className="ik-btn ik-btn-danger" onClick={() => clearKey(pk, label)} disabled={isBusy}>
                <FaTrashAlt /> Remove
              </button>
            )}
          </div>
        ) : (
          <div className="ik-edit-block">
            <div className="ik-input-wrap">
              <input
                type={showText ? "text" : "password"}
                className="ik-input"
                placeholder={`Paste ${label} API key`}
                value={draft}
                autoComplete="off"
                spellCheck={false}
                onChange={(e) => setEditDraft((p) => ({ ...p, [pk]: e.target.value }))}
              />
              <button
                className="ik-reveal-btn"
                onClick={() => setReveal((p) => ({ ...p, [pk]: !p[pk] }))}
                title={showText ? "Hide" : "Show"}
              >
                {showText ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
            <div className="ik-row-actions">
              <button className="ik-btn ik-btn-primary" onClick={() => saveKey(pk)} disabled={isBusy || !draft.trim()}>
                <FaSave /> {isBusy ? "Saving…" : "Save"}
              </button>
              <button className="ik-btn ik-btn-ghost" onClick={() => cancelEdit(pk)} disabled={isBusy}>
                <FaTimes /> Cancel
              </button>
            </div>
          </div>
        )}

        {keyError[pk] && <p className="ik-field-error">{keyError[pk]}</p>}
      </div>
    );
  };

  const renderAiCard = (provDef) => {
    const { key, label, abbrev } = provDef;
    const status    = keyStatus[key] || { source: "none" };
    const isOpen    = expanded.has(key);
    const isDefault = defaultAiProvider === key;
    const hasKey    = status.source !== "none";
    const models    = aiModels[key] || [];
    const modLoading = aiModelsLoading[key];
    const modError  = aiModelsError[key];
    const saving    = aiSaving === key;
    const saveMsg   = aiSaveMsg[key] || "";
    const curModel  =
      selectedModel[key] !== undefined
        ? selectedModel[key]
        : isDefault
        ? defaultAiModel || ""
        : "";

    return (
      <div key={key} className={`ik-card${isOpen ? " ik-open" : ""}${isDefault ? " ik-is-default" : ""}`}>
        <button className="ik-card-row" onClick={() => toggleCard(key, "ai")}>
          <Monogram abbrev={abbrev} />
          <div className="ik-card-info">
            <span className="ik-card-name">{label}</span>
            <span className="ik-card-sub">{hasKey ? "Key configured" : "No key set"}</span>
          </div>
          <div className="ik-card-end">
            {isDefault && <span className="ik-tag-default">Default</span>}
            <span className={`ik-src-tag ${SOURCE_BADGE[status.source].cls}`}>
              {SOURCE_BADGE[status.source].label}
            </span>
            {isOpen ? <FaChevronUp className="ik-chevron" /> : <FaChevronDown className="ik-chevron" />}
          </div>
        </button>

        {isOpen && (
          <div className="ik-card-body">
            {renderKeySection(key, label)}

            <div className="ik-separator" />

            <div className="ik-model-section">
              <span className="ik-field-label">Model</span>
              {!hasKey ? (
                <p className="ik-hint-text">Set an API key above to load available models.</p>
              ) : (
                <div className="ik-model-row">
                  <select
                    className="ik-select"
                    value={curModel}
                    onChange={(e) => setSelectedModel((p) => ({ ...p, [key]: e.target.value }))}
                    disabled={modLoading}
                  >
                    <option value="">{modLoading ? "Loading models…" : "— use provider default —"}</option>
                    {models.map((m) => (
                      <option key={m.id} value={m.id}>{m.name}</option>
                    ))}
                  </select>
                  <button
                    className="ik-btn ik-btn-ghost ik-icon-btn"
                    onClick={() => fetchModels(key)}
                    disabled={modLoading}
                    title="Refresh model list"
                  >
                    <FaSync className={modLoading ? "ik-spin" : ""} />
                  </button>
                </div>
              )}
              {modError && <p className="ik-field-error">{modError}</p>}
            </div>

            <div className="ik-card-footer">
              <button
                className={`ik-btn ${isDefault ? "ik-btn-default-on" : "ik-btn-default"}`}
                onClick={() => makeAiDefault(key)}
                disabled={saving || !hasKey}
              >
                <FaCheck />
                {saving ? "Saving…" : isDefault ? "Update Default" : "Make Default"}
              </button>
              {saveMsg && (
                <span className={saveMsg.startsWith("Error") ? "ik-msg-err" : "ik-msg-ok"}>
                  {saveMsg}
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderSandboxCard = (provDef) => {
    const { key, label, abbrev } = provDef;
    const status    = keyStatus[key] || { source: "none" };
    const isOpen    = expanded.has(key);
    const isDefault = sandboxDefault === key;
    const hasKey    = status.source !== "none";
    const saving    = sandboxSaving === key;
    const saveMsg   = sandboxMsg[key] || "";

    return (
      <div key={key} className={`ik-card${isOpen ? " ik-open" : ""}${isDefault ? " ik-is-default" : ""}`}>
        <button className="ik-card-row" onClick={() => toggleCard(key, "sandbox")}>
          <Monogram abbrev={abbrev} />
          <div className="ik-card-info">
            <span className="ik-card-name">{label}</span>
            <span className="ik-card-sub">{hasKey ? "Key configured" : "No key set"}</span>
          </div>
          <div className="ik-card-end">
            {isDefault && <span className="ik-tag-default">Default</span>}
            <span className={`ik-src-tag ${SOURCE_BADGE[status.source].cls}`}>
              {SOURCE_BADGE[status.source].label}
            </span>
            {isOpen ? <FaChevronUp className="ik-chevron" /> : <FaChevronDown className="ik-chevron" />}
          </div>
        </button>

        {isOpen && (
          <div className="ik-card-body">
            {renderKeySection(key, label)}
            <div className="ik-card-footer">
              <button
                className={`ik-btn ${isDefault ? "ik-btn-default-on" : "ik-btn-default"}`}
                onClick={() => makeSandboxDefault(key)}
                disabled={saving || !hasKey}
              >
                <FaCheck />
                {saving ? "Saving…" : isDefault ? "Default Sandbox" : "Make Default"}
              </button>
              {saveMsg && (
                <span className={saveMsg.startsWith("Error") ? "ik-msg-err" : "ik-msg-ok"}>
                  {saveMsg}
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderKeyOnlyCard = (provDef) => {
    const { key, label, abbrev } = provDef;
    const status = keyStatus[key] || { source: "none" };
    const isOpen = expanded.has(key);
    const hasKey = status.source !== "none";

    return (
      <div key={key} className={`ik-card${isOpen ? " ik-open" : ""}`}>
        <button className="ik-card-row" onClick={() => toggleCard(key, "key-only")}>
          <Monogram abbrev={abbrev} />
          <div className="ik-card-info">
            <span className="ik-card-name">{label}</span>
            <span className="ik-card-sub">{hasKey ? "Key configured" : "No key set"}</span>
          </div>
          <div className="ik-card-end">
            <span className={`ik-src-tag ${SOURCE_BADGE[status.source].cls}`}>
              {SOURCE_BADGE[status.source].label}
            </span>
            {isOpen ? <FaChevronUp className="ik-chevron" /> : <FaChevronDown className="ik-chevron" />}
          </div>
        </button>
        {isOpen && (
          <div className="ik-card-body">
            {renderKeySection(key, label)}
          </div>
        )}
      </div>
    );
  };

  const visibleCategories =
    activeCat === "all" || !scopedCategories.some((c) => c.id === activeCat)
      ? scopedCategories
      : scopedCategories.filter((c) => c.id === activeCat);

  /* ── JSX ── */
  return (
    <div className="ik-page">

      {/* ── Category filter ── */}
      {showFilterBar && (
        <div className="ik-filter-bar">
          <label className="ik-filter-label" htmlFor="ik-cat-select">Category</label>
          <select
            id="ik-cat-select"
            className="ik-cat-select"
            value={activeCat}
            onChange={(e) => setActiveCat(e.target.value)}
          >
            <option value={ALL_OPTION.id}>{ALL_OPTION.title}</option>
            {scopedCategories.map((c) => (
              <option key={c.id} value={c.id}>{c.title}</option>
            ))}
          </select>
        </div>
      )}

      {loading     && <div className="ik-state-msg">Loading…</div>}
      {globalError && <div className="ik-state-err">{globalError}</div>}

      {/* ── Sections ── */}
      {/* ── AI kill switch (own row; only when the AI category is in scope) ── */}
      {scopedCategories.some((c) => c.id === "ai") && (
        <section className="ik-section">
          <div className="ik-section-hd">
            <h2 className="ik-section-title">AI Kill Switch</h2>
            <p className="ik-section-desc">
              Master control for every AI-assisted feature across the platform.
            </p>
          </div>

          <div className={`ik-ai-master${aiEnabled ? "" : " ik-ai-master-off"}`}>
            <div className="ik-ai-master-icon">
              <FaRobot />
            </div>
            <div className="ik-ai-master-info">
              <div className="ik-ai-master-title">
                AI-Assisted Features
                <span className={`ik-ai-master-pill ${aiEnabled ? "on" : "off"}`}>
                  {aiEnabled ? "Enabled" : "Disabled"}
                </span>
              </div>
              <p className="ik-ai-master-desc">
                {aiEnabled
                  ? "AI is powering email verdicts, the Phishy assistant, query generation, remediation plans and threat insights. Turn off to make the entire platform rely only on deterministic, programmatic analysis."
                  : "AI is turned off platform-wide. Every feature is running on deterministic, programmatic logic only — no provider is contacted. Existing API keys and the selected provider below are preserved."}
              </p>
              {aiEnabledMsg && (
                <span className={aiEnabledMsg.startsWith("Error") ? "ik-msg-err" : "ik-msg-ok"}>
                  {aiEnabledMsg}
                </span>
              )}
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={aiEnabled}
              aria-label="Toggle AI-assisted features platform-wide"
              className={`ik-ai-switch ${aiEnabled ? "on" : "off"}`}
              onClick={toggleAiEnabled}
              disabled={aiEnabledSaving}
            >
              <span className="ik-ai-switch-knob" />
            </button>
          </div>
        </section>
      )}

      {visibleCategories.map((cat) => (
        <section key={cat.id} className="ik-section">
          <div className="ik-section-hd">
            <h2 className="ik-section-title">{cat.title}</h2>
            <p className="ik-section-desc">{cat.description}</p>
          </div>

          <div className="ik-card-list">
            {cat.providers.map((provDef) => {
              if (cat.type === "ai")      return renderAiCard(provDef);
              if (cat.type === "sandbox") return renderSandboxCard(provDef);
              return renderKeyOnlyCard(provDef);
            })}
          </div>

        </section>
      ))}
    </div>
  );
}
